<script lang="ts" setup>
import { computed, inject, onActivated, onDeactivated, ref, watch } from "vue"
import { useFormStore } from "@stores/form.store"
import { useField, useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"
import { boolean, object, string } from "yup"
import { TRACKING_EVENTS } from "@constant"
import { TrackingPlugin } from "@plugins/tracking.plugin"

const { $track } = inject<TrackingPlugin>("tracking")!

const formStore = useFormStore()
const {
    snapshot,
    cstbRecord,
    isFromUrlAndPdlFound,
    isFromUrl,
    send,
} = formStore

const options = ref([
    {
        name: "Je ne connais pas mon numéro de compteur",
        value: true,
    },
    {
        name: "Je connais mon numéro de compteur",
        value: false,
    },
])

/**
 * Indique si la machine d'état est dans l'étape de recherche automatique.
 */
const isInAutoSearchStep = computed(() =>
    snapshot.value.matches({
        Step1: { FormAddress: "autosearch" },
    })
)

// ==============================
// Gestion du schéma de validation
// ==============================

// 🧾 Schéma de validation
const { errors, handleSubmit } = useForm({
    validationSchema: toTypedSchema(
        object({
            autosearch: boolean().default(isInAutoSearchStep.value),
            manual: boolean().default(!!snapshot.value.context.address?.street),
            address: object({
                street: string()
                    .required("Adresse requise")
                    .default(snapshot.value.context.address.street),
                zipcode: string()
                    .required("Code postal requis")
                    .default(snapshot.value.context.address.zipcode),
                city: string()
                    .required("Ville requise")
                    .default(snapshot.value.context.address.city),
            }).default(
                snapshot.value.context.address
                    ? {
                          street: snapshot.value.context.address.street,
                          zipcode: snapshot.value.context.address.zipcode,
                          city: snapshot.value.context.address.city,
                      }
                    : null
            ),
            lastname: string()
                .required("Nom requis")
                .default(snapshot.value.context.lastname),
            firstname: string()
                .required("Prénom requis")
                .default(snapshot.value.context.firstname),
            username: string()
                .email("Email invalide")
                .required("Email requis")
                .default(snapshot.value.context.username),
            phone: string()
                .transform((value) => {
                    if (value === undefined || value === null || value === "") {
                        return value
                    }
                    const formatted = formatFrenchPhoneToMask(String(value))
                    return formatted || String(value).trim()
                })
                .test(
                    "is-valid",
                    "Numéro de téléphone invalide",
                    function (value: string | undefined) {
                        if (!value) return true
                        return /^\d{2} \d{2} \d{2} \d{2} \d{2}$/.test(value)
                    }
                )
                .required("Téléphone requis")
                .default(formatFrenchPhoneToMask(snapshot.value.context.phone)),

            pdl_idsource: string()
                .when("autosearch", {
                    is: (val: boolean) => !val,
                    then: (schema) =>
                        schema.required("Numéro de compteur requis"),
                })
                .matches(/^\d{14}$/, {
                    message: "Le numéro de compteur doit contenir 14 chiffres",
                    excludeEmptyString: true,
                })
                .default(snapshot.value.context.pdl_idsource),
        })
    ),
})

const onSubmit = handleSubmit(onSuccess, onError)

function onError() {
    $track(TRACKING_EVENTS.FORM_INAVLID, {
        formId: snapshot.value.context.formId,
        step: snapshot.value.context.step + 1,
    })
}

function normalizePdlId(raw: string | undefined | null) {
    if (raw === undefined || raw === null) return ""
    return String(raw).replace(/\s/g, "")
}

/**
 * Formate un numéro français pour le masque « 99 99 99 99 99 » (10 chiffres).
 * L'API peut renvoyer +33…, 06… sans espaces, etc.
 */
function formatFrenchPhoneToMask(raw: string | undefined | null): string {
    if (raw === undefined || raw === null) return ""
    const digits = String(raw).replace(/\D/g, "")
    if (digits.length === 0) return ""

    let ten = digits
    if (ten.startsWith("33") && ten.length >= 11) {
        ten = "0" + ten.slice(2, 12)
    }
    if (ten.length === 9 && /^[67]/.test(ten)) {
        ten = "0" + ten
    }

    if (ten.length !== 10) {
        return String(raw).trim()
    }

    return ten.match(/\d{2}/g)!.join(" ")
}

async function onSuccess(values: Record<string, any>) {
    const pdlFromForm = normalizePdlId(values.pdl_idsource)
    const pdlFromContext = normalizePdlId(snapshot.value.context.pdl_idsource)
    const pdl_idsource = pdlFromForm || pdlFromContext

    const merged: Record<string, unknown> = {
        ...values,
        pdl_idsource: pdl_idsource || values.pdl_idsource,
    }

    // Met à jour les champs dans le context
    ;[
        "address",
        "lastname",
        "firstname",
        "username",
        "pdl_idsource",
        "phone",
    ].forEach((key) => {
        send({ type: "UPDATE_FIELD", field: key, value: merged[key] })
    })

    // Si on a un numéro de compteur (formulaire ou dossier CSTB / tracking), on passe à l'étape suivante
    if (pdl_idsource) {
        send({ type: "SUBMIT" })
        return
    }

    // Si on est en mode autosearch et qu'on a pas de pdl_idsource
    if (isInAutoSearchStep.value && !pdl_idsource) {
        send({ type: "SEARCH_PDL" })
    }
}

// 🔁 Champs liés au store via VeeValidate
const { value: autosearch } = useField<boolean>("autosearch")
const { value: manual } = useField<boolean>("manual")
const { value: lastname } = useField<string>("lastname")
const { value: firstname } = useField<string>("firstname")
const { value: username } = useField<string>("username")
const { value: phone } = useField<string>("phone")

watch(
    () => snapshot.value.context.phone,
    (val) => {
        const formatted = formatFrenchPhoneToMask(val)
        if (formatted && /^\d{2} \d{2} \d{2} \d{2} \d{2}$/.test(formatted)) {
            phone.value = formatted
        }
    },
    { immediate: true }
)

const { value: address } = useField<object>("address")

const { value: street } = useField<string>("address.street")
const { value: zipcode } = useField<string>("address.zipcode")
const { value: city } = useField<string>("address.city")

const { value: pdl_idsource } = useField<string>("pdl_idsource")

/**
 * On écoute les changements sur le champ pdl_idsource
 * 1 - On met à jour le champ pdl_idsource dans le formulaire
 * 2 - Si on est en mode autosearch et qu'on a un pdl_idsource, on passe à l'étape suivante
 * (doit rester après useField("pdl_idsource") : immediate exécute le callback au setup)
 */
watch(
    () => snapshot.value.context.pdl_idsource,
    (val) => {
        pdl_idsource.value = val
        if (
            snapshot.value.matches({ Step1: { FormAddress: "autosearch" } }) &&
            !!val
        ) {
            send({ type: "SUBMIT" })
        }
    },
    { immediate: true }
)

const hasAddressError = computed(() => {
    return Object.keys(errors.value).some((key) => {
        return key.startsWith("address")
    })
})

const getSentence = computed(() => {
    if (isFromUrlAndPdlFound && cstbRecord) {
        const companyName = cstbRecord?._id_company?.name

        let sentence =
            "Bonne nouvelle !<br>En tant que client ##, nous connaissons déjà votre numéro de compteur.<br>Cliquez simplement sur le bouton pour confirmer et continuer"
        switch (true) {
            case companyName.includes("EDF"):
                sentence = sentence.replace("##", "EDF")
                break
            default:
                sentence = sentence.replace("##", companyName)
                break
        }

        return sentence
    }
    return "Grâce à votre adresse, nous allons essayer d'identifier votre numéro de compteur d'électricité."
})

/**
 * On écoute les changements sur la valeur de l'état de la machine
 *
 * 1 -  Si on est à l'étape "idle", on redirige vers le bon mode "autosearch" ou "manual"
 */
watch(
    () => snapshot.value,
    () => {
        if (
            snapshot.value.matches({
                Step1: { FormAddress: "idle" },
            })
        ) {
            send({ type: "REDIRECT" })
        }
        if (
            snapshot.value.matches({
                Step1: { FormAddress: "autosearch" },
            })
        ) {
            autosearch.value = true
        }
        if (
            snapshot.value.matches({
                Step1: { FormAddress: "manualsearch" },
            })
        ) {
            autosearch.value = false
        }
    },
    { immediate: true }
)

/** Seul le numéro de compteur reste saisissable : passage en saisie manuelle du PRM. */
watch(
    () => ({
        locked: formStore.isDisableUserInfo,
        auto: isInAutoSearchStep.value,
    }),
    ({ locked, auto }) => {
        if (locked && auto) {
            send({ type: "MANUAL_SEARCH" })
        }
    },
    { immediate: true }
)

const timeCompletion = ref(new Date().getTime())

onActivated(() => {
    timeCompletion.value = new Date().getTime()
    $track(TRACKING_EVENTS.STEP_ENTERED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
    })
})

onDeactivated(() => {
    $track(TRACKING_EVENTS.STEP_COMPLETED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
        timeCompletion: (
            (new Date().getTime() - timeCompletion.value) /
            1000
        ).toFixed(1),
    })
})

const visible = ref(false)
</script>

<template>
    <FormCard
        class="form-address"
        title="Adresse et compteur d'électricité"
        :footer-btn="false"
    >
        <!-- Sélection entre recherche ou saisie manuel du numéro du compteur -->
        <BaseSelectButton
            v-if="
                !isFromUrlAndPdlFound && !formStore.isDisableUserInfo
            "
            class="form-address__select"
            :options="options"
            option-label="name"
            option-value="value"
            :model-value="isInAutoSearchStep"
            :pt="{
                pcToggleButton: {
                    root: 'custom-togglebutton',
                    label: 'custom-togglebutton-label',
                },
            }"
            @update:modelValue="
                (val: boolean) => {
                    $track(TRACKING_EVENTS.CLICK_BUTTON, {
                        buttonId: 'search-type-pdl',
                        buttonName: val ? options[0].name : options[1].name,
                    })
                    send({ type: `${val ? 'AUTO' : 'MANUAL'}_SEARCH` })
                }
            "
        />
        <!-- Phrase d'aide -->
        <div
            class="form-address__sentence"
            v-if="isInAutoSearchStep || isFromUrlAndPdlFound"
        >
            <img
                src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,q_auto,f_auto/v1745852810/icon_smile_g3q7sb.png"
                alt="Smiley icon"
                loading="lazy"
                class="form-address__smile"
            />
            <p v-html="getSentence" class="text-center"></p>
        </div>
        <!-- Formulaire d'adresse / PDL et information utilisateur -->
        <form @submit.prevent="onSubmit" class="form-address__info">
            <BaseMessage
                v-if="snapshot.context.error_pdl_search"
                severity="error"
                class="w-full"
                icon="pi pi-exclamation-triangle"
            >
                Nous n'avons pas trouvé de correspondance entre votre adresse,
                votre nom et un compteur électrique. Pourriez-vous nous aider en
                saisissant le numéro de votre compteur électrique ?</BaseMessage
            >
            <!-- Numéro de PDL manuellement -->
            <fieldset
                class="form-address-info__pdl"
                v-if="!isInAutoSearchStep || formStore.isDisableUserInfo"
            >
                <BaseInputWrapper
                    class="w-full"
                    label="Numéro de compteur (PRM ou PDL)"
                    input-id="pdl"
                    :error="errors.pdl_idsource"
                >
                    <BaseInputText
                        id="pdl"
                        placeholder="14 chiffres"
                        v-model="pdl_idsource"
                        :invalid="errors.pdl_idsource"
                    />
                </BaseInputWrapper>
                <div class="form-address-info__pdl-button">
                    <img
                        src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,q_auto,f_auto/v1746454894/4ee22d4cc24d61ab00885c63006a113b89a54e11_zk5gqb.png"
                        alt="Icône d'information"
                        loading="lazy"
                    />
                    <BaseButton
                        text
                        label="Où trouver mon numéro de compteur ?"
                        @click="
                            () => {
                                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                                    buttonId: 'how-to-find-pdl',
                                    buttonName:
                                        'Où trouver mon numéro de compteur ?',
                                })
                                visible = !visible
                            }
                        "
                    ></BaseButton>
                    <DialogPdl v-model:visible="visible"></DialogPdl>
                </div>
            </fieldset>
            <!-- Formulaire d'adresse autocomplete -->
            <InputAddress
                v-if="!manual && !isFromUrlAndPdlFound"
                class="w-full"
                id="address"
                label="Adresse"
                :error="
                    hasAddressError
                        ? 'Sélectionner une adresse dans la liste des propositions'
                        : null
                "
                :invalid="hasAddressError"
                :disabled="formStore.isDisableUserInfo"
                v-model="address"
                placeholder="Numéro et nom de voie"
            />

            <!-- Formulaire d'adrese manuellement -->
            <fieldset
                v-else-if="!isFromUrlAndPdlFound"
                class="form-address-info__manual"
            >
                <BaseInputWrapper
                    input-id="address"
                    :error="errors['address.street']"
                    label="Adresse"
                >
                    <BaseInputText
                        v-model="street"
                        id="address"
                        :invalid="errors['address.street']"
                        :disabled="formStore.isDisableUserInfo"
                        placeholder="Numéro et nom de voie"
                    />
                </BaseInputWrapper>

                <BaseInputWrapper
                    :error="errors['address.zipcode']"
                    label="Code postal"
                    input-id="zipcode"
                >
                    <BaseInputText
                        id="zipcode"
                        placeholder="Saisissez"
                        v-model="zipcode"
                        :invalid="errors['address.zipcode']"
                        :disabled="formStore.isDisableUserInfo"
                    />
                </BaseInputWrapper>

                <BaseInputWrapper
                    label="Ville"
                    input-id="city"
                    :error="errors['address.city']"
                >
                    <BaseInputText
                        id="city"
                        placeholder="Saisissez"
                        v-model="city"
                        :invalid="errors['address.city']"
                        :disabled="formStore.isDisableUserInfo"
                    />
                </BaseInputWrapper>
            </fieldset>

            <!-- Formulaire sur les informations de l'utilisateur -->
            <fieldset class="form-address-info__user-infos">
                <BaseInputWrapper
                    label="Nom"
                    input-id="lastname"
                    :error="errors.lastname"
                >
                    <BaseInputText
                        id="lastname"
                        placeholder="Saisissez"
                        v-model="lastname"
                        :invalid="errors.lastname"
                        :disabled="formStore.isDisableUserInfo"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    label="Prénom"
                    :error="errors.firstname"
                    input-id="firstname"
                >
                    <BaseInputText
                        id="firstname"
                        placeholder="Saisissez"
                        v-model="firstname"
                        :invalid="errors.firstname"
                        :disabled="formStore.isDisableUserInfo"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    label="Téléphone"
                    :error="errors.phone"
                    input-id="phone"
                >
                    <BaseInputMask
                        id="phone"
                        placeholder="Saisissez"
                        v-model="phone"
                        mask="99 99 99 99 99"
                        :invalid="errors.phone"
                        :disabled="formStore.isDisableUserInfo"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    v-if="!isFromUrlAndPdlFound && !isFromUrl"
                    label="Email"
                    :error="errors.username"
                    input-id="username"
                >
                    <BaseInputText
                        id="username"
                        placeholder="email@email.com"
                        v-model="username"
                        :invalid="errors.username"
                        :disabled="formStore.isDisableUserInfo"
                    />
                </BaseInputWrapper>
            </fieldset>
            <!-- Formulaire sur les informations de l'utilisateur -->
            <BaseButton
                :label="
                    !isFromUrlAndPdlFound
                        ? 'Je confirme mon adresse'
                        : 'Je confirme mon numéro de compteur'
                "
                icon="pi pi-arrow-circle-right"
                icon-pos="right"
                :loading="
                    snapshot.matches({
                        Step1: { FormAddress: { autosearch: 'search_pdl' } },
                    })
                "
                class="w-full my-2"
                type="submit"
                @click="
                    () => {
                        $track(TRACKING_EVENTS.CLICK_BUTTON, {
                            buttonId: 'submit-form-address',
                            buttonName: !isFromUrlAndPdlFound
                                ? 'Je confirme mon adresse'
                                : 'Je confirme mon numéro de compteur',
                        })
                    }
                "
            ></BaseButton>
        </form>

        <!-- Bouton pour changer de form -->
        <BaseButton
            v-if="!isFromUrlAndPdlFound"
            class="w-fit mx-auto"
            :disabled="formStore.isDisableUserInfo"
            :label="
                manual
                    ? 'Chercher mon adresse dans la liste des propositions'
                    : 'Mon adresse est introuvable'
            "
            icon="pi pi-exclamation-circle"
            icon-pos="left"
            variant="text"
            @click="
                () => {
                    $track(TRACKING_EVENTS.CLICK_BUTTON, {
                        buttonId: 'unknown-address',
                        buttonName: manual
                            ? 'Chercher mon adresse dans la liste des propositions'
                            : 'Mon adresse est introuvable',
                    })
                    manual = !manual
                }
            "
        >
        </BaseButton>
    </FormCard>
</template>

<style scoped>
@import "@styles/breakpoints.css";
.form-address {
    --gap-form: 3.5rem;
    --padding-form: 3.5rem;
    &:deep(.p-card-content) {
        display: flex;
        flex-direction: column;
        gap: var(--gap-form);
        padding-top: var(--padding-form);

        @media (--small-viewport) {
            --gap-form: 2.5rem;
            --padding-form: 2.5rem;
        }
    }

    .form-address__sentence {
        display: flex;
        padding: 0rem 1rem;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        align-self: stretch;

        p {
            color: #092754;
            font-size: 0.875rem;
            font-weight: 600;
        }

        @media (--small-viewport) {
            padding: 0.5rem;
            border-radius: 0.5rem;
            background: #f6f9fc;
        }
    }

    .form-address__smile {
        width: 1.5rem;
        height: 1.5rem;
    }

    .form-address__wrapper-form {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .form-address__info {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        .form-address-info__fields {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
            align-self: stretch;

            fieldset {
                width: 100%;
            }
        }

        .form-address-info__autocomplete {
            &:deep(input) {
                width: 100%;
            }
        }

        /* Formulaire adresse manuel */
        .form-address-info__manual {
            display: grid;
            grid-template-columns: 1fr;

            @media not (--small-viewport) {
                grid-template-columns: repeat(3, minmax(0, 1fr));
                .base-input-wrapper:first-child {
                    grid-column: 1 / 4;
                }
                .base-input-wrapper:nth-child(3) {
                    grid-column: 2 / 4;
                }
            }

            gap: 1rem;
        }

        /* Formulaire des informations de l'utilisateurs */
        .form-address-info__user-infos {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            flex-direction: column;
            gap: 1rem;
            align-self: stretch;

            @media (--small-viewport) {
                grid-template-columns: 1fr;
            }
        }

        .form-address-info__pdl {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
        .form-address-info__pdl-button {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            img {
                width: 1.5rem;
                height: 1.5rem;
            }

            .p-button {
                height: fit-content;
                --p-button-padding-y: 0;
                --p-button-padding-x: 0.5rem;
                --p-button-text-primary-hover-background: transparent;
                --p-button-text-primary-active-background: transparent;
            }
        }
    }
}
</style>
